import { api } from "@/services/api";
import { i18nToast } from "@/utils/i18nToast";
import {
  AlertCircle,
  CheckCircle2,
  FileText,
  Lock,
  Shield,
  Upload,
  X,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import type { StoredDocument } from "@/types/document";

interface DocumentUploadProps {
  onUploadComplete?: (documents: StoredDocument[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  allowedCategories?: string[];
  defaultCategory?: string;
  defaultPrivate?: boolean;
}

interface UploadFile {
  id: string;
  rawFile: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error: string;
}

function extractUploadErrorMessage(error: any): string {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    "Upload failed"
  );
}

const DOCUMENT_CATEGORIES = [
  { value: "HEALTH_RECORD", label: "Health Record", icon: "🏥" },
  { value: "VACCINATION", label: "Vaccinations", icon: "💉" },
  { value: "PRESCRIPTION", label: "Prescriptions", icon: "💊" },
  { value: "INSURANCE", label: "Insurance", icon: "🛡️" },
  { value: "TRAVEL", label: "Travel Documents", icon: "✈️" },
  { value: "OTHER", label: "Other", icon: "📄" },
];

// The server's allowlist (backend/src/utils/uploadType.ts), which decides by
// the file's bytes; this copy only saves a round trip for an obvious mismatch.
// Word and text files were listed here once, but nothing that can hold markup
// is stored any more: a stored upload is served from the app's own origin.
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/heic",
  "image/heif",
];

// Several browsers report an empty type for HEIC photos, so the extension is
// accepted as well; the server still checks the bytes.
const ALLOWED_EXTENSIONS = [".heic", ".heif"];

const UNSUPPORTED_TYPE_MESSAGE =
  "This file type is not supported. Please upload a PDF, JPEG, PNG, WebP or HEIC file.";

export const SecureDocumentUpload: React.FC<DocumentUploadProps> = ({
  onUploadComplete,
  maxFiles = 10,
  maxFileSize = 100, // 100MB default
  allowedCategories,
  defaultCategory = "OTHER",
  defaultPrivate = true,
}) => {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [category, setCategory] = useState(defaultCategory);
  const [isPrivate, setIsPrivate] = useState(defaultPrivate);
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [currentTag, setCurrentTag] = useState("");

  const categories = allowedCategories
    ? DOCUMENT_CATEGORIES.filter((cat) => allowedCategories.includes(cat.value))
    : DOCUMENT_CATEGORIES;

  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file type
      const lowerName = file.name.toLowerCase();
      if (
        !ALLOWED_FILE_TYPES.includes(file.type) &&
        !ALLOWED_EXTENSIONS.some((ext) => lowerName.endsWith(ext))
      ) {
        return `File type ${file.type || "unknown"} is not allowed. ${UNSUPPORTED_TYPE_MESSAGE}`;
      }

      // Check file size (convert MB to bytes)
      if (file.size > maxFileSize * 1024 * 1024) {
        return `File is too large (max. ${maxFileSize}MB)`;
      }

      return null;
    },
    [maxFileSize],
  );

  const handleFileSelect = useCallback(
    (selectedFiles: FileList | File[]) => {
      const fileArray = Array.from(selectedFiles);

      if (files.length + fileArray.length > maxFiles) {
        i18nToast.error(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const validFiles: UploadFile[] = [];

      fileArray.forEach((file) => {
        const error = validateFile(file);
        const uploadFile: UploadFile = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          rawFile: file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: error ? "error" : "pending",
          error: error || "",
          progress: 0,
        };

        // Create preview for images
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            uploadFile.preview = e.target?.result as string;
            setFiles((prev) =>
              prev.map((f) => (f.id === uploadFile.id ? uploadFile : f)),
            );
          };
          reader.readAsDataURL(file);
        }

        validFiles.push(uploadFile);
      });

      setFiles((prev) => [...prev, ...validFiles]);
    },
    [files.length, maxFiles, validateFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);

      const droppedFiles = e.dataTransfer.files;
      handleFileSelect(droppedFiles);
    },
    [handleFileSelect],
  );

  const removeFile = useCallback((fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const addTag = useCallback(() => {
    if (currentTag.trim() && !tags.includes(currentTag.trim())) {
      setTags((prev) => [...prev, currentTag.trim()]);
      setCurrentTag("");
    }
  }, [currentTag, tags]);

  const removeTag = useCallback((tagToRemove: string) => {
    setTags((prev) => prev.filter((tag) => tag !== tagToRemove));
  }, []);

  const uploadFiles = async () => {
    if (files.length === 0 || isUploading) return;

    setIsUploading(true);

    try {
      const filesToUpload = files.filter((f) => f.status !== "error");

      // Update file statuses to uploading
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: f.status === "error" ? "error" : ("uploading" as const),
          progress: 0,
        })),
      );

      const uploadedDocuments: StoredDocument[] = [];

      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append("file", file.rawFile, file.name);
        formData.append("type", category);
        formData.append("description", description);

        const response = await api.post("/documents/upload", formData);

        const result = response.data;
        if (!result.success) {
          throw new Error(result.error?.message || "Upload failed");
        }

        if (result.data?.document) {
          // `api.post` returns an untyped body; this is the trust boundary.
          uploadedDocuments.push(result.data.document as StoredDocument);
        }
      }

      // Update file statuses to success
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: f.status === "uploading" ? ("success" as const) : f.status,
          progress: 100,
        })),
      );

      i18nToast.success(
        `${uploadedDocuments.length} document(s) uploaded successfully`,
      );

      if (uploadedDocuments.length > 0) {
        onUploadComplete?.(uploadedDocuments);
      }

      // Clear form after successful upload
      setTimeout(() => {
        setFiles([]);
        setDescription("");
        setTags([]);
      }, 2000);
    } catch (error: any) {
      console.error("Upload error:", error);
      const uploadErrorMessage = extractUploadErrorMessage(error);

      // Update file statuses to error
      setFiles((prev) =>
        prev.map((f) => ({
          ...f,
          status: f.status === "uploading" ? ("error" as const) : f.status,
          error: uploadErrorMessage,
        })),
      );

      i18nToast.error("Upload failed: " + uploadErrorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case "uploading":
        return (
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        );
      case "success":
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <FileText className="w-4 h-4 text-gray-400" />;
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
          <Shield className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Secure document upload
          </h3>
          <p className="text-sm text-gray-600">
            Sent over an encrypted connection and stored privately
          </p>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
          ${
            isDragOver
              ? "border-blue-500 bg-blue-50"
              : "border-gray-300 hover:border-gray-400"
          }
        `}
        role="button"
        tabIndex={0}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => document.getElementById("file-input")?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            document.getElementById("file-input")?.click();
          }
        }}
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept={[...ALLOWED_FILE_TYPES, ...ALLOWED_EXTENSIONS].join(",")}
          onChange={(e) => e.target.files && handleFileSelect(e.target.files)}
          className="hidden"
        />

        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h4 className="text-lg font-medium text-gray-900 mb-2">
          Drop files here or click
        </h4>
        <p className="text-gray-600 mb-2">
          Supported formats: PDF, JPEG, PNG, WebP, HEIC
        </p>
        <p className="text-sm text-gray-500">
          Max. {maxFiles} files, up to {maxFileSize}MB each
        </p>
      </div>

      {/* Upload Settings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Category Selection */}
        <div>
          <label
            htmlFor="document-category"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Category
          </label>
          <select
            id="document-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-[42px] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {categories.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.icon} {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Privacy Setting */}
        <div>
          <div className="block text-sm font-medium text-gray-700 mb-2">
            Privacy
          </div>
          <div className="flex items-center gap-4">
            <label htmlFor="privacy-private" className="flex items-center">
              <input
                id="privacy-private"
                type="radio"
                checked={isPrivate}
                onChange={() => setIsPrivate(true)}
                className="mr-2 text-blue-600"
              />
              <Lock className="w-4 h-4 mr-1 text-green-600" />
              <span className="text-sm">Private (encrypted)</span>
            </label>
            <label htmlFor="privacy-public" className="flex items-center">
              <input
                id="privacy-public"
                type="radio"
                checked={!isPrivate}
                onChange={() => setIsPrivate(false)}
                className="mr-2 text-blue-600"
              />
              <span className="text-sm">Public</span>
            </label>
          </div>
        </div>
      </div>

      {/* Description */}
      <div>
        <label
          htmlFor="document-description"
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          Description (optional)
        </label>
        <textarea
          id="document-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Document description..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          maxLength={1000}
        />
      </div>

      {/* Tags */}
      <div>
        <div className="block text-sm font-medium text-gray-700 mb-2">
          Tags (optional)
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="ml-1 w-3 h-3 text-blue-600 hover:text-blue-800"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={currentTag}
            onChange={(e) => setCurrentTag(e.target.value)}
            placeholder="Add tag..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyPress={(e) => e.key === "Enter" && addTag()}
          />
          <button
            onClick={addTag}
            disabled={!currentTag.trim()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
          >
            Add
          </button>
        </div>
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700">Selected files</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {files.map((file) => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                {file.preview ? (
                  <img
                    src={file.preview}
                    alt=""
                    className="w-10 h-10 object-cover rounded"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                  {file.error && (
                    <p className="text-xs text-red-600">{file.error}</p>
                  )}
                  {file.status === "uploading" && (
                    <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                      <div
                        className="bg-blue-600 h-1 rounded-full transition-all duration-300"
                        style={{ width: `${file.progress || 0}%` }}
                      />
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {getStatusIcon(file.status)}
                  <button
                    onClick={() => removeFile(file.id)}
                    disabled={isUploading}
                    className="w-6 h-6 text-gray-400 hover:text-red-600 disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Button */}
      {files.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={uploadFiles}
            disabled={isUploading || files.every((f) => f.status === "error")}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload {files.length} file(s)
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
